import React, { Component } from 'react';

import { reloadCompany } from 'common/actions/company';
import AJAX from 'common/AJAX';
import type { Company } from 'common/api/endpoints/companies';
import { StaticColors } from 'common/colors/constants';
import { type HexColor, isHexColor } from 'common/colors/utils';
import { CompanyContext } from 'common/containers/CompanyContainer';
import { OpenModalContext, type OpenModalContextType } from 'common/containers/ModalContainer';
import { ShowToastContext, type ShowToastFn, ToastTypes } from 'common/containers/ToastContainer';
import { type Viewer, ViewerContext } from 'common/containers/ViewerContainer';
import connect from 'common/core/connect';
import * as FileUploader from 'common/FileUploader';
import Helmet from 'common/helmets/Helmet';
import Image from 'common/Image';
import FileInput from 'common/inputs/FileInput';
import SubdomainInputV2 from 'common/inputs/SubdomainInputV2';
import AccessModal from 'common/modals/AccessModal';
import withAccessControl from 'common/routing/withAccessControl';
import Strings from 'common/Strings';
import { cn } from 'common/style/utils';
import BrandColorPicker from 'common/subdomain/admin/AdminGeneralBrandingSettings/BrandColorPicker';
import ColorAccessibilityChecker from 'common/subdomain/admin/AdminGeneralBrandingSettings/ColorAccessibilityChecker';
import ThemeSelect from 'common/subdomain/admin/ThemeSelect';
import Button from 'common/uiV3/Button';
import Field from 'common/uiV3/Field';
import Flex from 'common/uiV3/Flex';
import HStack from 'common/uiV3/HStack';
import Input from 'common/uiV3/Input';
import Spinner from 'common/uiV3/Spinner';
import VStack from 'common/uiV3/VStack';
import devURL from 'common/util/devURL';
import hasPermission from 'common/util/hasPermission';
import { RoutePermissions, testEveryPermission } from 'common/util/permissions';
import type { Theme } from 'common/util/theme';
import withContexts from 'common/util/withContexts';
import validateInput from 'common/validateInput';
import { ImageIcon } from 'lucide-react';
import PropTypes from 'prop-types';
import { compose } from 'redux';

type Form = {
  name: string;
  subdomain: string;
  tint: HexColor;
  theme: Theme;
};

type State = {
  edited: boolean;
  editing: boolean;
  nameError: string | null;
  subdomainError: string | null;
  colorError: string | null;
  hasEdits: boolean;
  faviconURL: string | null;
  logoURL: string | null;
  uploadingFavicon: boolean;
  uploadingLogo: boolean;
  form: Form;
};

type Props = {
  company: Company;
  location: {
    pathname: string;
  };
  openModal: OpenModalContextType;
  showToast: ShowToastFn;
  reloadCompany: () => void;
  router: {
    push: (path: string) => void;
    replace: (path: string) => void;
  };
  viewer: Viewer;
};

class AdminGeneralBrandingSettings extends Component<Props, State> {
  static propTypes = {
    company: PropTypes.shape({
      _id: PropTypes.string,
      faviconURL: PropTypes.string,
      logoURL: PropTypes.string,
      name: PropTypes.string,
      subdomain: PropTypes.string,
      tintColor: PropTypes.string,
    }),
    location: PropTypes.shape({
      pathname: PropTypes.string,
    }),
    openModal: PropTypes.func,
    router: PropTypes.object,
    viewer: PropTypes.shape({
      email: PropTypes.string,
    }),
  };

  state = {
    edited: false,
    editing: false,
    nameError: null,
    subdomainError: null,
    colorError: null,
    hasEdits: false,
    faviconURL: this.props.company.faviconURL,
    logoURL: this.props.company.logoURL,
    uploadingFavicon: false,
    uploadingLogo: false,
    form: {
      name: this.props.company.name,
      subdomain: this.props.company.subdomain,
      tint: this.props.company.tintColor,
      theme: this.props.company.theme,
    },
  };

  componentDidMount() {
    const { company, openModal, router, viewer } = this.props;

    if (hasPermission('manageCompanyProfile', company, viewer)) {
      return;
    }

    router.replace('/admin');
    openModal(
      AccessModal,
      {
        requiredPermissions: ['manageCompanyProfile'],
      } satisfies React.ComponentProps<typeof AccessModal>,
      {
        allowRouteChange: true,
      },
    );
  }

  onFormChange = (edits: Partial<Form>) => {
    this.setState((state) => ({
      edited: false,
      hasEdits: true,
      nameError: edits.name !== undefined ? null : state.nameError,
      subdomainError: edits.subdomain !== undefined ? null : state.subdomainError,
      colorError: edits.tint !== undefined ? null : state.colorError,
      form: {
        ...state.form,
        ...edits,
      },
    }));
  };

  onNewFavicon = (file: File, base64URL: string) => {
    if (!file) {
      return;
    }

    const image = new window.Image();
    image.src = base64URL;
    image.onload = async () => {
      const { viewer, showToast } = this.props;
      const { width, height } = image;

      if (width !== height) {
        showToast('Please upload a square image.', ToastTypes.error);
        return;
      }

      this.setState({
        uploadingFavicon: true,
      });

      let faviconURL = null;
      let error = null;

      try {
        faviconURL = await FileUploader.upload(file, viewer);
      } catch (err) {
        error = err;
      }

      if (error || !faviconURL) {
        const message =
          error instanceof Error
            ? error.message
            : 'Something went wrong, please try again or contact support';

        this.setState({
          uploadingFavicon: false,
        });
        showToast(message, ToastTypes.error);
        return;
      }

      this.setState({
        edited: false,
        hasEdits: true,
        faviconURL,
        uploadingFavicon: false,
      });
    };
  };

  onNewLogo = (file: File, base64URL: string) => {
    if (!file) {
      return;
    }

    const image = new window.Image();
    image.src = base64URL;
    image.onload = async () => {
      const { viewer, showToast } = this.props;
      const { width, height } = image;
      if (width !== height) {
        this.props.showToast('Please upload a square image.', ToastTypes.error);
        return;
      }

      this.setState({
        uploadingLogo: true,
      });

      let logoURL = null;
      let error = null;

      try {
        logoURL = await FileUploader.upload(file, viewer);
      } catch (err) {
        error = err;
      }

      if (error || !logoURL) {
        const message =
          error instanceof Error
            ? error.message
            : 'Something went wrong, please try again or contact support';

        this.setState({
          uploadingLogo: false,
        });
        showToast(message, ToastTypes.error);
        return;
      }

      this.setState({
        edited: false,
        hasEdits: true,
        logoURL,
        uploadingLogo: false,
      });
    };
  };

  onSubmit = async () => {
    const {
      faviconURL,
      logoURL,
      form: { name, subdomain, tint: tintColor, theme },
    } = this.state;

    const nameError = !validateInput.companyName(name)
      ? 'Please enter a valid company name (1-100)'
      : null;
    const subdomainError = !validateInput.subdomain(subdomain)
      ? 'Please enter a valid subdomain (1-30)'
      : null;
    const colorError = !validateInput.hexColor(tintColor)
      ? 'Please enter a valid brand color (e.g. #abc or #abcdef)'
      : null;

    if (nameError || subdomainError || colorError) {
      this.setState({
        nameError,
        subdomainError,
        colorError,
      });
      return;
    }

    this.setState({
      editing: true,
      nameError: null,
      subdomainError: null,
      colorError: null,
    });

    const { company } = this.props;
    AJAX.post(
      '/api/company/edit',
      {
        faviconURL,
        logoURL,
        name,
        subdomain,
        theme,
        tintColor,
      },
      (response) => {
        if (response !== 'success') {
          let responseObject;
          try {
            responseObject = JSON.parse(response);
          } catch (_e) {
            responseObject = { error: 'server error' };
          }

          let error = Strings.miscError;
          if (responseObject.error && responseObject.error === 'subdomain taken') {
            error = 'This subdomain is taken';
            this.setState({
              editing: false,
              subdomainError: error,
            });
            return;
          }

          this.setState({
            editing: false,
          });
          this.props.showToast(error, ToastTypes.error);
          return;
        }

        this.setState({
          edited: true,
          editing: false,
          hasEdits: false,
        });

        if (company.subdomain === subdomain) {
          this.props.reloadCompany();
        } else {
          const {
            location: { pathname },
          } = this.props;
          const protocol = window.location.protocol + '//';
          const host = subdomain + '.canny.io';
          const newURL = devURL(protocol + host + pathname);
          window.location.assign(newURL);
        }
      },
    );
  };

  renderLogoSettings() {
    const { logoURL, uploadingLogo } = this.state;

    const logo = logoURL ? (
      <Image loader={this.renderImgContainer(<Spinner size="large" />)} src={logoURL} />
    ) : (
      this.renderImgContainer(<ImageIcon className="text-muted-foreground" />)
    );

    return (
      <HStack className="items-center gap-4">
        <div className="size-12 [&_img]:size-full">{logo}</div>
        <FileInput
          onFile={this.onNewLogo}
          defaultStyle={false}
          value={
            <Button variant="outlined" isLoading={uploadingLogo}>
              Update logo
            </Button>
          }
        />
        {this.renderGuidelines('192 x 192 pixels', '1:1')}
      </HStack>
    );
  }

  renderFaviconSettings() {
    const { faviconURL, uploadingFavicon } = this.state;
    const favicon = faviconURL ? (
      <Image loader={this.renderImgContainer(<Spinner />, 'rounded-xs')} src={faviconURL} />
    ) : (
      this.renderImgContainer(<ImageIcon className="text-muted-foreground" />, 'rounded-xs')
    );

    return (
      <HStack className="items-center gap-4">
        <div className="px-2 size-8 [&_img]:size-full">{favicon}</div>
        <FileInput
          onFile={this.onNewFavicon}
          defaultStyle={false}
          value={
            <Button variant="outlined" isLoading={uploadingFavicon}>
              Update icon
            </Button>
          }
        />
        {this.renderGuidelines('48 x 48 pixels', '1:1')}
      </HStack>
    );
  }

  renderImgContainer(children: React.ReactNode, className: string = 'rounded-full') {
    return (
      <Flex className={cn('items-center justify-center bg-muted size-full', className)}>
        {children}
      </Flex>
    );
  }

  renderGuidelines(size: string, ratio: string) {
    return (
      <div className="text-secondary-foreground">
        <div className="text-sm leading-tight">Recommended size: {size}</div>
        <div className="text-sm leading-tight">Ratio: {ratio}</div>
      </div>
    );
  }

  onSubdomainChange = (value: string) => {
    this.onFormChange({ subdomain: value });
  };

  render() {
    const { company } = this.props;
    const {
      edited,
      editing,
      hasEdits,
      nameError,
      subdomainError,
      colorError,
      form: { name, subdomain, tint, theme },
    } = this.state;

    return (
      <div>
        <Helmet title="Branding | Company Setting | Canny" />
        <VStack className="max-w-[720px] gap-8">
          <VStack className="gap-4">
            {this.renderLogoSettings()}
            {this.renderFaviconSettings()}
            <Field>
              <Field.Label>Name</Field.Label>
              <Field.Content>
                <Input
                  value={name}
                  onChange={(value) => this.onFormChange({ name: value })}
                  size="large"
                  placeholder="Company name"
                />
              </Field.Content>
              {nameError && <Field.Error>{nameError}</Field.Error>}
            </Field>
            <Field>
              <Field.Label>Subdomain</Field.Label>
              <Field.Content>
                <SubdomainInputV2
                  defaultValue={subdomain}
                  value={subdomain}
                  size="large"
                  placeholder="Subdomain"
                  onChange={this.onSubdomainChange}
                />
              </Field.Content>
              {subdomainError && <Field.Error>{subdomainError}</Field.Error>}
            </Field>
          </VStack>
          <VStack className="gap-4">
            <h2 className="text-lg font-semibold">Theme</h2>
            <Field orientation="horizontal" className="justify-between">
              <VStack className="gap-2">
                <Field.Label>Feedback portal theme</Field.Label>
                <Field.Description className="text-base">
                  Select an interface theme for your public feedback portal.
                </Field.Description>
              </VStack>
              <Field.Content className="items-end">
                <ThemeSelect
                  value={theme}
                  onValueChange={(theme) => this.onFormChange({ theme })}
                />
              </Field.Content>
            </Field>
            <BrandColorPicker
              color={tint}
              onColorPicked={(tint) => this.onFormChange({ tint })}
              error={colorError}
            />
            <ColorAccessibilityChecker
              foreground={isHexColor(tint) ? tint : company.tintColor}
              backgrounds={[StaticColors.white, StaticColors.darkThemeBackground].filter(
                isHexColor,
              )}
            />
          </VStack>
          <Button
            disabled={!hasEdits}
            isLoading={editing}
            onClick={this.onSubmit}
            className="self-end">
            {edited ? 'Saved' : 'Save'}
          </Button>
        </VStack>
      </div>
    );
  }
}

export default compose<React.FC<Props>>(
  connect(null, { reloadCompany }),
  withAccessControl(
    testEveryPermission(RoutePermissions.adminSettings.general.branding),
    '/admin/settings',
    { forwardRef: true },
  ),
  withContexts(
    {
      company: CompanyContext,
      openModal: OpenModalContext,
      showToast: ShowToastContext,
      viewer: ViewerContext,
    },
    {
      forwardRef: true,
    },
  ),
)(AdminGeneralBrandingSettings);
